import axios from 'axios';

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    role: string;
    roles?: string[];
    isEmailVerified: boolean;
}

interface AuthResponse {
    token: string;
    refreshToken: string;
    user: User;
    message?: string;
    requiresConfirmation?: boolean;
    email?: string;
}

interface RegisterData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    role?: 'user' | 'author';
    organization?: string;
    position?: string;
}

interface VerifyEmailResponse {
    message: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
console.log('API URL:', API_URL);

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Добавляем токен к каждому запросу
api.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('token');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('Отправка запроса:', {
        url: config.url,
        method: config.method,
        headers: config.headers
    });
    return config;
});

// Добавляем обработчик ответов
api.interceptors.response.use(
    (response) => {
        console.log('Получен ответ:', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
        return response;
    },
    (error) => {
        console.error('Ошибка запроса:', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });

        // Если сервер недоступен
        if (!error.response) {
            console.error('Сервер недоступен');
            return Promise.reject(new Error('Сервер недоступен. Пожалуйста, проверьте подключение к интернету и попробуйте снова.'));
        }

        // Если токен истек — не делаем hard-reload на /login (иначе бесконечный цикл)
        const requestUrl = String(error.config?.url || '');
        const isAuthEndpoint =
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/refresh-token') ||
            requestUrl.includes('/auth/profile') ||
            requestUrl.includes('/auth/login-with-code');
        const alreadyOnLogin =
            typeof window !== 'undefined' && window.location.pathname.startsWith('/login');

        if (error.response.status === 401 && !isAuthEndpoint) {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('refreshToken');
            if (!alreadyOnLogin) {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export const auth = {
    register: (data: RegisterData) =>
        api.post<AuthResponse>('/auth/register', data),
    login: (data: { email: string; password: string }) =>
        api.post<AuthResponse>('/auth/login', data).catch(error => {
            console.error('Ошибка при входе:', error);
            return Promise.reject(error);
        }),
    getProfile: () => 
        api.get<User>('/auth/profile'),
    refreshToken: (refreshToken: string) =>
        api.post<AuthResponse>('/auth/refresh-token', { refreshToken }),
    verifyEmail: (token: string) =>
        api.get<VerifyEmailResponse>(`/auth/verify-email?token=${token}`),
    resendRegistrationCode: (data: { email: string }) =>
        api.post<{ message: string }>('/auth/resend-registration-code', data),
    confirmRegistration: (data: { email: string; code: string }) =>
        api.post<AuthResponse>('/auth/confirm-registration', data),
    sendLoginCode: (data: { email: string }) =>
        api.post<{ message: string }>('/auth/send-login-code', data),
    loginWithCode: (data: { email: string; code: string }) =>
        api.post<AuthResponse>('/auth/login-with-code', data),
};

export default api; 